/**
 * /legal/privacy — Privacy Policy (AU Privacy Act 1988 + APP-aware).
 *
 * Source contract: docs/ux/journeys/J-X13-privacy-and-tos-acceptance.md
 * § Privacy Policy content requirements (AU). Each numbered subsection there
 * maps 1:1 to a heading below.
 *
 * Regulatory posture (G1 ratified 2026-05-28 — Australia first):
 *   - Cites Australian Privacy Act 1988 + Australian Privacy Principles
 *     (APP 1, 5, 8, 11, 12, 13).
 *   - References OAIC for complaints.
 *   - Does NOT claim GDPR compliance — EU rollout adds J-X-D02 + J-X-D27 first.
 *
 * Currently rendered as a draft — SME / legal counsel review before launch.
 * Bumping `PRIVACY_VERSION` (in @bimassess/db) on any material change
 * re-prompts existing users via the stale-consent modal (J-X13 § Steps existing user).
 */

import type { ReactElement } from 'react';
import { PRIVACY_VERSION } from '@bimassess/db/legal-versions';
import { LegalPage } from './LegalPage.tsx';

export function PrivacyPolicyPage(): ReactElement {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Privacy Policy"
      version={PRIVACY_VERSION}
      draft
    >
      <p>
        BIMAssess is operated from Australia. This Privacy Policy describes how
        we collect, hold, use, and disclose personal information in accordance
        with the <strong>Australian Privacy Act 1988 (Cth)</strong> and the{' '}
        <strong>Australian Privacy Principles (APPs)</strong>. It is written
        in the spirit of <strong>APP 1.4</strong> — open and transparent
        management of personal information.
      </p>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        1. Who we are
      </h2>
      <p>
        BIMAssess is an assessment platform for BIM (Building Information
        Modelling) practitioners. The operating entity is currently in the
        process of formal registration; once registered the entity name and
        Australian Business Number (ABN) will be listed here.
      </p>
      <p>
        Contact for privacy enquiries:{' '}
        <a href="mailto:support@bimassess.com" className="underline">
          support@bimassess.com
        </a>
        .
      </p>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        2. Kinds of personal information we collect
      </h2>
      <ul className="list-disc space-y-1 pl-6">
        <li>Email address</li>
        <li>Name</li>
        <li>
          Account events — sign-in timestamps, password resets, IP address
          (used for security and audit; APP 11)
        </li>
        <li>Test responses, answers, and computed results</li>
        <li>Optional uploaded artefacts (e.g. Revit models) when required</li>
      </ul>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        3. How we collect and hold information
      </h2>
      <p>
        We collect information directly from you when you sign up, sign in,
        and take assessments. Session state is managed by Better-Auth.
        Account data is stored in a managed Postgres database (Neon). Uploaded
        artefacts are stored in object storage (Cloudflare R2).
      </p>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        4. Purposes of collection
      </h2>
      <ul className="list-disc space-y-1 pl-6">
        <li>Account management and authentication</li>
        <li>Delivering assessments and computing results</li>
        <li>Issuing certificates and credential records</li>
        <li>Audit, fraud prevention, and security (APP 11)</li>
      </ul>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        5. Consequences if information is not collected
      </h2>
      <p>
        Email and name are required to create an account; without them you
        cannot use the service. Specific test responses are required to be
        scored; without them the result has no candidate-side basis.
      </p>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        6. Disclosures to third parties
      </h2>
      <p>
        We disclose limited personal information to the following providers
        strictly for the purposes named:
      </p>
      <ul className="list-disc space-y-1 pl-6">
        <li>
          <strong>Hosting and database:</strong> Neon (managed Postgres),
          Cloudflare R2 (object storage)
        </li>
        <li>
          <strong>Transactional email:</strong> the email-delivery provider
          configured for verification and password-reset messages
        </li>
        <li>
          <strong>Error monitoring:</strong> Sentry (only where consented per
          our cookie policy — see{' '}
          <a href="/legal/cookies" className="underline">
            Cookie Policy
          </a>
          )
        </li>
      </ul>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        7. Overseas disclosures (APP 8)
      </h2>
      <p>
        Our database (Neon) and object storage (Cloudflare R2) regions are
        chosen to keep data resident in Australia or, where unavoidable, in a
        comparable privacy jurisdiction. The specific regions in use at any
        given time are listed on request. We take reasonable steps under
        APP 8 to ensure overseas recipients handle personal information in a
        manner consistent with the APPs.
      </p>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        8. Access and correction (APP 12 + APP 13)
      </h2>
      <p>
        You have the right to access and correct personal information we hold
        about you. For routine access, your account-activity summary is
        available at{' '}
        <a href="/me/activity" className="underline">
          /me/activity
        </a>
        . For full access or correction requests not satisfied by the
        self-service surface, contact{' '}
        <a href="mailto:support@bimassess.com" className="underline">
          support@bimassess.com
        </a>
        . We will respond within a reasonable timeframe (30 days standard).
      </p>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        9. Complaints
      </h2>
      <p>
        Internal complaints process: write to{' '}
        <a href="mailto:support@bimassess.com" className="underline">
          support@bimassess.com
        </a>{' '}
        with the subject "Privacy complaint". We will acknowledge within 5
        business days and respond substantively within 30 days.
      </p>
      <p>
        External complaints: you may lodge a complaint with the{' '}
        <strong>Office of the Australian Information Commissioner (OAIC)</strong>
        :
      </p>
      <ul className="list-disc space-y-1 pl-6">
        <li>
          Web:{' '}
          <a
            href="https://www.oaic.gov.au/privacy/privacy-complaints"
            className="underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            oaic.gov.au/privacy/privacy-complaints
          </a>
        </li>
        <li>Phone: 1300 363 992 (Australia)</li>
      </ul>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        10. Changes to this policy
      </h2>
      <p>
        We version this policy. Material changes prompt all existing users to
        review and re-accept on next sign-in. The current version string is
        shown at the top of this page.
      </p>
    </LegalPage>
  );
}
